import { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, Lock, RefreshCw, WandSparkles, Target } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import type { GlobalCategory, CategoryType } from '@/types/category.types';
import type { CostCategoryType } from '@/types/budget.types';
import {
  MOCK_NOMINAL_CODES,
  MOCK_SALES_NOMINAL_CODES,
  type NominalCode,
} from '@/lib/cost-category-mapping-storage';
import {
  NominalCodePicker,
  COST_RECENTLY_USED,
  SALES_RECENTLY_USED,
} from '@/components/settings/NominalCodePicker';
import {
  getDataMode,
  fetchSalesNominalCodes,
  fetchCostNominalCodes,
  fetchCategoryNominalMappings,
  fetchCategoryTypes,
  type NominalCodeApiItem,
} from '@/lib/data-service';
import { useCompany } from '@/contexts/CompanyContext';

const MAX_CATEGORY_CODE_LENGTH = 50;

/** Fallback abbreviation map used when API types are unavailable */
const TYPE_ABBREV_FALLBACK: Record<CostCategoryType, string> = {
  labour: 'LAB',
  expenses: 'EXP',
  subcontractors: 'SUB',
  materials: 'MAT',
  equipment: 'EQP',
  overhead: 'OVH',
  contingency: 'CTG',
};

/**
 * Generate a category code from the category name and type.
 * Format: <NAME_PREFIX>-<TYPE_ABBREV>-<SEQ>  e.g. TES-LAB-001
 *
 * NAME_PREFIX = first 3 alphanumeric chars of the name (uppercase).
 * TYPE_ABBREV = 3-char abbreviation of the cost-category type.
 *
 * The function checks `existingCodes` (case-insensitive) to avoid duplicates
 * and guarantees the result never exceeds `MAX_CATEGORY_CODE_LENGTH`.
 */
function generateCategoryCode(
  categoryName: string,
  categoryType: CostCategoryType,
  existingCodes: string[],
  abbrevMap: Record<string, string> = {},
): string {
  // First 3 alphanumeric characters of the name, uppercased
  const nameChars = categoryName
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  const namePrefix = (nameChars.length > 0 ? nameChars.slice(0, 3) : 'CAT').padEnd(3, 'X');

  const typeAbbrev = abbrevMap[categoryType] || TYPE_ABBREV_FALLBACK[categoryType] || 'CAT';

  const lowerExisting = new Set(existingCodes.map(c => c.toLowerCase()));

  // Try sequential numbers until we find one that doesn't clash
  for (let seq = 1; seq <= 9999; seq++) {
    const seqStr = seq.toString().padStart(3, '0');
    let candidate = `${namePrefix}-${typeAbbrev}-${seqStr}`;

    // Ensure we never exceed the NVARCHAR(50) limit
    if (candidate.length > MAX_CATEGORY_CODE_LENGTH) {
      candidate = candidate.slice(0, MAX_CATEGORY_CODE_LENGTH);
    }

    if (!lowerExisting.has(candidate.toLowerCase())) {
      return candidate;
    }
  }

  // Extremely unlikely fallback
  return `${namePrefix}-${Date.now()}`.slice(0, MAX_CATEGORY_CODE_LENGTH);
}

const FALLBACK_CATEGORY_TYPES: { value: CostCategoryType; label: string }[] = [
  { value: 'labour', label: 'Labour' },
  { value: 'expenses', label: 'Expenses' },
  { value: 'subcontractors', label: 'Subcontractors' },
  { value: 'materials', label: 'Materials' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'overhead', label: 'Overhead' },
  { value: 'contingency', label: 'Contingency' },
];

/** Convert API nominal code items to the NominalCode shape the picker expects */
function apiItemToNominalCode(item: NominalCodeApiItem, type: 'Cost' | 'Sales'): NominalCode {
  return {
    nominalCode: item.code,
    nominalName: item.name,
    nominalType: type,
    debit: item.code,
    debitAccountName: item.name,
    credit: '',
    creditAccountName: '',
    vat: '',
    vatAccountName: '',
    sortKey: item.sortKey ?? item.code,
  };
}

interface CategoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category?: GlobalCategory | null;
  onSave: (data: {
    name: string;
    code?: string;
    type: CostCategoryType;
    description?: string;
    isDefault: boolean;
    costCodes: string[];
    salesCodes: string[];
    allCostCodes: NominalCode[];
    allSalesCodes: NominalCode[];
  }) => Promise<void>;
  existingCategories?: GlobalCategory[];
  /** Optional initial values for pre-filling the form in "add" mode (not used in edit mode) */
  initialValues?: {
    name?: string;
    code?: string;
    type?: CostCategoryType;
    lockType?: boolean;
  };
  /** The ID of the current cascade target category (used to lock Type field and show badge in edit mode) */
  cascadeTargetCategoryId?: number | null;
}

export function CategoryFormDialog({
  open,
  onOpenChange,
  category,
  onSave,
  existingCategories,
  initialValues,
  cascadeTargetCategoryId,
}: CategoryFormDialogProps) {
  const isEditing = !!category;
  const isCodeLocked = isEditing && (category?.usageCount ?? 0) > 0;
  const isCascadeTarget = isEditing && cascadeTargetCategoryId != null && category?.id === cascadeTargetCategoryId;
  const { currentCompany } = useCompany();

  // ── Details tab state ──────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [type, setType] = useState<CostCategoryType>('labour');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ── Nominal Codes tab state ────────────────────────────────────────────────
  const [costCodes, setCostCodes] = useState<string[]>([]);
  const [salesCodes, setSalesCodes] = useState<string[]>([]);

  // ── Category types from API ─────────────────────────────────────────────────
  const [apiCategoryTypes, setApiCategoryTypes] = useState<CategoryType[]>([]);

  const categoryTypeOptions = useMemo(() => {
    if (apiCategoryTypes.length > 0) {
      return apiCategoryTypes.map(ct => ({ value: ct.code as CostCategoryType, label: ct.label }));
    }
    return FALLBACK_CATEGORY_TYPES;
  }, [apiCategoryTypes]);

  const typeAbbrevMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const ct of apiCategoryTypes) {
      map[ct.code] = ct.abbreviation;
    }
    return map;
  }, [apiCategoryTypes]);

  // ── Nominal codes from API ─────────────────────────────────────────────────
  const [allCostCodes, setAllCostCodes] = useState<NominalCode[]>([]);
  const [allSalesCodes, setAllSalesCodes] = useState<NominalCode[]>([]);
  const [isLoadingCodes, setIsLoadingCodes] = useState(false);
  const [codesError, setCodesError] = useState<string | null>(null);

  // ── Load nominal codes from API with retry ─────────────────────────────────
  const loadNominalCodes = useCallback(async (signal?: { cancelled: boolean }) => {
    const mode = getDataMode();
    if (mode === 'demo') {
      setAllCostCodes(MOCK_NOMINAL_CODES);
      setAllSalesCodes(MOCK_SALES_NOMINAL_CODES);
      setCodesError(null);
      return;
    }

    const companyId = currentCompany?.id;
    if (!companyId) {
      setAllCostCodes([]);
      setAllSalesCodes([]);
      setCodesError('No company selected. Please select a company to load nominal codes.');
      return;
    }

    setIsLoadingCodes(true);
    setCodesError(null);

    const numericCompanyId = Number(companyId);
    const maxRetries = 2;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const [costItems, salesItems] = await Promise.all([
          fetchCostNominalCodes(numericCompanyId),
          fetchSalesNominalCodes(numericCompanyId),
        ]);

        if (signal?.cancelled) return;

        setAllCostCodes(costItems.map(item => apiItemToNominalCode(item, 'Cost')));
        setAllSalesCodes(salesItems.map(item => apiItemToNominalCode(item, 'Sales')));
        setCodesError(null);
        setIsLoadingCodes(false);
        return;
      } catch (err) {
        if (signal?.cancelled) return;

        if (attempt < maxRetries) {
          const delay = 1000 * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
          if (signal?.cancelled) return;
          continue;
        }

        console.error('Failed to load nominal codes from Financials API:', err);
        setCodesError('Failed to load nominal codes from Financials. Please try again.');
        setAllCostCodes([]);
        setAllSalesCodes([]);
      }
    }

    if (!signal?.cancelled) setIsLoadingCodes(false);
  }, [currentCompany?.id]);

  useEffect(() => {
    if (!open) return;

    const signal = { cancelled: false };
    loadNominalCodes(signal);

    fetchCategoryTypes()
      .then((types) => {
        if (!signal.cancelled) setApiCategoryTypes(types);
      })
      .catch(() => { /* fall back to hardcoded */ });

    return () => { signal.cancelled = true; };
  }, [open, loadNominalCodes]);

  // ── Populate on open ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    if (category) {
      setName(category.name);
      setCode(category.code || '');
      setType(category.type);
      setDescription(category.description || '');
      setIsDefault(category.isDefault);

      // Load saved nominal code mappings from API
      const mode = getDataMode();
      if (mode !== 'demo') {
        fetchCategoryNominalMappings(category.id)
          .then((mappings) => {
            setCostCodes(mappings.filter(m => m.direction === 'cost').map(m => m.nominalCode));
            setSalesCodes(mappings.filter(m => m.direction === 'sales').map(m => m.nominalCode));
          })
          .catch((err) => {
            console.error('Failed to load nominal mappings:', err);
            setCostCodes([]);
            setSalesCodes([]);
          });
      } else {
        setCostCodes([]);
        setSalesCodes([]);
      }
    } else {
      setName(initialValues?.name ?? '');
      setCode(initialValues?.code ?? '');
      setType(initialValues?.type ?? 'labour');
      setDescription('');
      setIsDefault(false);
      setCostCodes([]);
      setSalesCodes([]);
    }
    setErrors({});
  }, [open, category, initialValues]);

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!type) {
      newErrors.type = 'Type is required';
    }
    if (!code.trim()) {
      newErrors.code = 'Code is required';
    } else {
      const duplicate = existingCategories?.find(
        c => c.code?.toLowerCase() === code.trim().toLowerCase() && c.id !== category?.id
      );
      if (duplicate) {
        newErrors.code = `Code "${code.trim()}" is already used by "${duplicate.name}"`;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSaving(true);
    try {
      await onSave({
        name: name.trim(),
        code: code.trim(),
        type,
        description: description.trim() || undefined,
        isDefault,
        costCodes,
        salesCodes,
        allCostCodes,
        allSalesCodes,
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save category:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Sales categories — only Labour, Expenses, Subcontractors, Materials, Equipment ──
  const showSalesTab = ['labour', 'expenses', 'subcontractors', 'materials', 'equipment'].includes(type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>{isEditing ? 'Edit Category' : 'Add Category'}</DialogTitle>
            {isCascadeTarget && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800 px-2 py-0.5 text-xs font-medium">
                      <Target className="h-3 w-3" />
                      Cascade Target
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This category is the active target for Resource Budget cascading.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <DialogDescription>
            {isEditing
              ? 'Update the details of this global budget category and assign nominal codes.'
              : 'Create a new global budget category available across all projects.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="details" className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
            {/* Nominal Codes tab hidden — values are not calculating correctly; to be revisited */}
            <TabsTrigger value="nominal-codes" className="flex-1 hidden">
              Nominal Codes
              {(costCodes.length > 0 || salesCodes.length > 0) && (
                <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold w-5 h-5">
                  {costCodes.length + salesCodes.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── Details tab ── */}
          <TabsContent value="details" className="space-y-4 pt-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="category-name">
                Name *
              </Label>
              <Input
                id="category-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Labour, Equipment, Travel"
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            {/* Type */}
            <div className="space-y-2">
              <Label htmlFor="category-type">
                Type *
              </Label>
              <Select value={type} onValueChange={(val) => setType(val as CostCategoryType)} disabled={(initialValues?.lockType && !category) || isCascadeTarget}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a type" />
                </SelectTrigger>
                <SelectContent>
                  {categoryTypeOptions.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {initialValues?.lockType && !category && (
                <p className="text-xs text-muted-foreground">Type is pre-selected for this category.</p>
              )}
              {isCascadeTarget && (
                <p className="text-xs text-muted-foreground">Type cannot be changed while this category is the Resource Budget cascade target. It must remain Labour.</p>
              )}
              {errors.type && (
                <p className="text-sm text-destructive">{errors.type}</p>
              )}
            </div>

            {/* Code */}
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="category-code">Code *</Label>
                {!isCodeLocked && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 rounded-sm text-muted-foreground hover:text-foreground"
                          disabled={!name.trim()}
                          onClick={() => {
                            if (!name.trim()) return;
                            const existingCodes = (existingCategories ?? [])
                              .filter(c => c.id !== category?.id)
                              .map(c => c.code ?? '')
                              .filter(Boolean);
                            const generated = generateCategoryCode(name, type, existingCodes, typeAbbrevMap);
                            setCode(generated);
                            setErrors(prev => {
                              const next = { ...prev };
                              delete next.code;
                              return next;
                            });
                          }}
                        >
                          <WandSparkles className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-sm">
                          {name.trim() ? 'Auto-generate code from category name' : 'Enter a category name first'}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              <div className="relative">
                <Input
                  id="category-code"
                  value={code}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.length <= MAX_CATEGORY_CODE_LENGTH) {
                      setCode(val);
                    }
                  }}
                  placeholder="e.g. LAB-001"
                  maxLength={MAX_CATEGORY_CODE_LENGTH}
                  disabled={isCodeLocked}
                  className={isCodeLocked ? 'pr-9 bg-muted' : ''}
                />
                {isCodeLocked && (
                  <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                )}
              </div>
              {isCodeLocked && (
                <p className="text-xs text-muted-foreground">
                  Code cannot be changed because this category is in use by {category?.usageCount} project(s).
                </p>
              )}
              {errors.code && (
                <p className="text-sm text-destructive">{errors.code}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="category-description">Description</Label>
              <Textarea
                id="category-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description of the category"
                rows={3}
              />
            </div>

            {/* Is Default */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="category-default"
                checked={isDefault}
                onCheckedChange={(checked) => setIsDefault(checked === true)}
              />
              <Label htmlFor="category-default" className="font-normal">
                Default category (pre-populated in new projects)
              </Label>
            </div>
          </TabsContent>

          {/* ── Nominal Codes tab (hidden) ── */}
          <TabsContent value="nominal-codes" className="pt-4 space-y-6 hidden">

            {isLoadingCodes ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Loading nominal codes from Financials...</span>
              </div>
            ) : codesError ? (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive flex items-center justify-between gap-3">
                <span>{codesError}</span>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-1.5 border-destructive/50 text-destructive hover:bg-destructive/10"
                  onClick={() => loadNominalCodes()}
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Retry
                </Button>
              </div>
            ) : (
              <>
                {/* Cost nominal codes */}
                <div className="space-y-3">
                  <div>
                    <Label>Cost Nominal Codes</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Assign expense or purchase nominal codes for cost tracking on this category.
                    </p>
                  </div>
                  <NominalCodePicker
                    allCodes={allCostCodes}
                    selected={costCodes}
                    onValueChange={setCostCodes}
                    recentlyUsedCodes={COST_RECENTLY_USED}
                    placeholder="Assign cost nominal codes…"
                    dialogTitle="Select Cost Nominal Code"
                  />
                </div>

                <Separator />

                {/* Sales nominal codes — only relevant for billable category types */}
                <div className="space-y-3">
                  <div>
                    <Label>
                      Sales Nominal Codes
                      {!showSalesTab && (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          (not applicable for {type} categories)
                        </span>
                      )}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Assign revenue nominal codes for T&amp;M billing on this category.
                    </p>
                  </div>
                  {showSalesTab ? (
                    <NominalCodePicker
                      allCodes={allSalesCodes}
                      selected={salesCodes}
                      onValueChange={setSalesCodes}
                      recentlyUsedCodes={SALES_RECENTLY_USED}
                      placeholder="Assign sales nominal codes…"
                      dialogTitle="Select Sales Nominal Code"
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      Overhead and Contingency categories are internal cost categories and do not
                      have sales nominal code mappings.
                    </p>
                  )}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button
                    onClick={handleSubmit}
                    disabled={isSaving || !name.trim() || !code.trim() || !type}
                    style={{ pointerEvents: (isSaving || !name.trim() || !code.trim() || !type) ? 'none' : undefined }}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : isEditing ? (
                      'Update Category'
                    ) : (
                      'Add Category'
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              {(!name.trim() || !code.trim() || !type) && (
                <TooltipContent>
                  <p>All required fields must be complete</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
